import { and, eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { useStore } from '@/lib/pocketbase'
import type { LocationGeometry, LocationPB } from '@/types/locations'
import type { Jobs } from '@/types/pbSchema'

export interface LocationCircle {
    id: string
    name: string
    latitude: number
    longitude: number
    radius: number
    jobId: string
    jobName: string
    jobStatus: string
}

export function useActiveLocations(orgId: string | null | undefined) {
    const [locationsCollection, jobsCollection] = useStore('locations', 'jobs')

    const { isLoading, data: locations = [] } = useLiveQuery(
        q => {
            if (!orgId) return undefined
            return q
                .from({ location: locationsCollection })
                .join({ job: jobsCollection }, ({ location, job }) => eq(location.job, job.id))
                .where(({ location, job }) =>
                    and(
                        eq(location.org, orgId),
                        eq(job?.status, 'ACTIVE'),
                        eq(location.inactive, false)
                    )
                )
                .orderBy(({ location }) => location.name, 'asc')
        },
        [orgId]
    )

    return {
        locations,
        isPending: isLoading,
    }
}

interface LocationJobRow {
    location: LocationPB
    job: Jobs
}

interface LocationWithGeometry {
    location: LocationPB & { geometry: LocationGeometry }
    job: Jobs
}

export function useLocationCircles(orgId: string | null | undefined) {
    const { locations, isPending } = useActiveLocations(orgId)

    const circles = useMemo(() => {
        return (locations as LocationJobRow[])
            .filter((row): row is LocationWithGeometry => {
                const location = row.location
                if (!location?.geometry) return false
                if (typeof location.geometry !== 'object') return false
                const geom = location.geometry as LocationGeometry
                if (geom.type !== 'Point') return false
                if (!Array.isArray(geom.coordinates)) return false
                if (geom.coordinates.length < 2) return false
                return true
            })
            .map((row): LocationCircle => {
                const location = row.location
                const job = row.job
                const geom = location.geometry
                return {
                    id: location.id,
                    name: location.name,
                    latitude: geom.coordinates[1],
                    longitude: geom.coordinates[0],
                    radius: geom.radius ?? 200,
                    jobId: job.id,
                    jobName: job.name ?? 'Unknown Job',
                    jobStatus: job.status ?? 'UNKNOWN',
                }
            })
    }, [locations])

    return {
        circles,
        isPending,
    }
}
